import { z } from "zod";

export const worksPermitStatusEnum = z.enum(["SUBMITTED", "UNDER_REVIEW", "APPROVED", "REJECTED", "COMPLETED"]);
export const maintenanceCategoryEnum = z.enum([
  "PLUMBING",
  "ELECTRICAL",
  "STRUCTURAL",
  "COMMON_AREA",
  "ELEVATOR",
  "SECURITY",
  "OTHER",
]);
export const priorityEnum = z.enum(["LOW", "MEDIUM", "HIGH", "URGENT"]);
export const maintenanceStatusEnum = z.enum(["OPEN", "ASSIGNED", "IN_PROGRESS", "RESOLVED", "CLOSED"]);
export const announcementCategoryEnum = z.enum([
  "GENERAL",
  "MAINTENANCE",
  "SECURITY",
  "FINANCIAL",
  "REGULATORY",
  "URGENT",
]);
export const announcementStatusEnum = z.enum(["DRAFT", "SENT", "PUBLISHED", "ARCHIVED"]);

// Kinds of documents a condominium modification request typically carries.
export const worksPermitDocKindEnum = z.enum([
  "PLANOS_CROQUIS",
  "DESCRIPCION_MATERIALES",
  "COTIZACION",
  "LICENCIA_CONTRATISTA",
  "SEGURO_CONTRATISTA",
  "PERMISO_MUNICIPAL",
  "FOTOS",
  "OTRO",
]);
export type WorksPermitDocKind = z.infer<typeof worksPermitDocKindEnum>;
export const WORKS_PERMIT_DOC_KINDS = worksPermitDocKindEnum.options;

export const permitAttachmentInput = z.object({
  kind: worksPermitDocKindEnum.default("OTRO"),
  title: z.string().trim().min(1, "Título requerido").max(200),
  fileUrl: z.string().url(),
});
export type PermitAttachmentInput = z.infer<typeof permitAttachmentInput>;

const optionalText = (max: number) => z.string().trim().max(max).optional().nullable();
// An empty field on the form means "not given", never an invalid email.
const emailOrEmpty = z
  .union([z.literal(""), z.string().trim().email("Correo inválido")])
  .nullable()
  .optional()
  .transform((v) => (v === "" ? null : v));

// Fields of the official "Solicitud de Permiso de Obras y/o Trabajos" form.
const permitFormFields = {
  applicantName: optionalText(120),
  applicantPhone: optionalText(40),
  applicantPhone2: optionalText(40),
  applicantEmail: emailOrEmpty,
  applicantEmail2: emailOrEmpty,
  contractorName: optionalText(120),
  contractorCompany: optionalText(120),
  contractorPhone: optionalText(40),
  contractorPhone2: optionalText(40),
  contractorEmail: emailOrEmpty,
  contractorEmail2: emailOrEmpty,
  plansDelivered: z.boolean().optional(),
  // "Yo, ___, certifico que conozco, acepto y me comprometo a cumplir con las normas…"
  rulesAccepted: z.boolean().optional(),
  rulesAcceptedName: optionalText(120),
  attachments: z.array(permitAttachmentInput).max(20).optional(),
};

export const createWorksPermitSchema = z.object({
  unitId: z.string().min(1, "Unidad requerida"),
  description: z.string().min(10, "Describa la obra (mínimo 10 caracteres)"),
  workType: z.string().min(1, "Tipo de obra requerido"),
  startDate: z.coerce.date(),
  estimatedEndDate: z.coerce.date(),
  ...permitFormFields,
  // Older clients: bare URLs become attachments of kind OTRO.
  attachmentUrls: z.array(z.string().url()).optional().default([]),
});

// Editing an existing request (resident own draft, or admin/on-behalf). Unit is
// immutable; only the request content and its supporting documents can change.
export const updateWorksPermitSchema = z.object({
  description: z.string().min(10, "Describa la obra (mínimo 10 caracteres)").optional(),
  workType: z.string().min(1, "Tipo de obra requerido").optional(),
  startDate: z.coerce.date().optional(),
  estimatedEndDate: z.coerce.date().optional(),
  ...permitFormFields,
  attachmentUrls: z.array(z.string().url()).optional(),
});

export const reviewWorksPermitSchema = z.object({
  decision: z.enum(["APPROVED", "REJECTED", "UNDER_REVIEW"]),
  reviewNotes: z.string().optional().nullable(),
});

export const createMaintenanceRequestSchema = z.object({
  unitId: z.string().optional().nullable(),
  category: maintenanceCategoryEnum,
  title: z.string().min(1, "Título requerido"),
  description: z.string().min(5, "Describa el problema"),
  priority: priorityEnum.optional().default("MEDIUM"),
  photoUrls: z.array(z.string().url()).optional().default([]),
});

export const updateMaintenanceRequestSchema = z.object({
  status: maintenanceStatusEnum.optional(),
  priority: priorityEnum.optional(),
  resolutionNotes: z.string().optional().nullable(),
  // Descriptive fields. Without them here the validator dropped every edit of the request
  // itself in silence: the reporter corrects their own while it is still open, the
  // administration and the board correct any.
  unitId: z.string().optional().nullable(),
  category: maintenanceCategoryEnum.optional(),
  title: z.string().min(1, "Título requerido").optional(),
  description: z.string().min(5, "Describa el problema").optional(),
  photoUrls: z.array(z.string().url()).optional(),
});

export const assignMaintenanceSchema = z.object({
  assignedToId: z.string().min(1),
});

export const createAnnouncementSchema = z.object({
  title: z.string().min(1),
  content: z.string().min(1),
  category: announcementCategoryEnum,
  // Validity window (calendar dates, both inclusive); either end may be left open.
  startsAt: z.coerce.date().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  attachmentUrls: z.array(z.string().url()).optional().default([]),
  // Also on the public landing page, to anyone without signing in.
  showOnPublicPage: z.boolean().optional().default(false),
});

// Who receives the email about an announcement: the chosen units' owners (all units when
// omitted, none when empty), the Junta Directiva when ticked, and extra addresses typed in.
// `publish` decides whether it is also published in the portal; false = email only (e.g. a
// draft sent to the Junta for review), and it stays a draft.
export const publishAnnouncementSchema = z.object({
  includeBoard: z.boolean().optional().default(false),
  // Each chosen unit's current tenant as well as its owner.
  includeTenants: z.boolean().optional().default(true),
  publish: z.boolean().optional().default(true),
  unitIds: z.array(z.string().min(1)).max(500).optional(),
  extraEmails: z
    .array(z.string().trim().toLowerCase().email("Correo inválido"))
    .max(50)
    .transform((list) => [...new Set(list)])
    .optional()
    .default([]),
});

// Send an announcement again to one recipient of an earlier send.
export const resendAnnouncementSchema = z.object({
  deliveryId: z.string().min(1),
});

export const updateAnnouncementSchema = createAnnouncementSchema.partial().extend({
  status: announcementStatusEnum.optional(),
});
